import { type UseToggleOptions, useToggle } from '@vueuse/core';
import type { MaybeRef } from 'vue';

export type Maybe<T> = T | null | undefined;

export type NumberKeyOf<T> = {
  [K in keyof T]: T[K] extends number ? K : never;
}[keyof T];

export type StringKeyOf<T> = {
  [K in keyof T]: T[K] extends string | null | undefined ? K : never;
}[keyof T];

export const chunkArray = <T>(myArray: T[], chunkSize: number) => {
  return myArray.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / chunkSize);
    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = [];
    }

    resultArray[chunkIndex].push(item);
    return resultArray;
  }, [] as T[][]);
};

export const groupBy = <T, TProp extends string | number | symbol>(
  array: T[],
  keySel: (i: T) => TProp,
): Record<TProp, T[]> => {
  return array.reduce(
    (groups, item) => {
      const key = keySel(item);
      const entry = groups[key] || (groups[key] = []);
      entry.push(item);
      return groups;
    },
    {} as Record<TProp, T[]>,
  );
};

/**
 * Uses equality to determine if param is null or undefined
 * @function
 * @param {any} o
 * @returns {boolean} is null or undefined
 */
export const isNullOrUndefined = (o: unknown): o is null | undefined => {
  return o == null;
};

export const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const useActionToggle = (initialValue: MaybeRef<boolean>, options: UseToggleOptions<true, false> = {}) => {
  const [value, toggle] = useToggle(initialValue, options);
  const run = async <T>(action: () => Promise<T>) => {
    toggle();
    try {
      await action();
    } finally {
      toggle();
    }
  };

  return {
    value,
    toggle,
    run,
  };
};
